import React, { useEffect, useRef, useState } from "react";
import { Route, useHistory } from "react-router";
import AddProduct from "./AddProduct";
import ReactLoading from "react-loading";
import { serverUrl } from "../../Utils/server";
import axios from "axios";
import FontAwesome from "react-fontawesome";
import Modal from "react-modal";
import { toast, ToastContainer, darkToast } from "../../Components/Toast";
import EditProduct from "./EditProduct";

function Products() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState([]);

  const [level1Id, setLevel1Id] = useState("");
  const [level2Id, setLevel2Id] = useState("");
  const [level3Id, setLevel3Id] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState("");

  const [deleteProduct, setDeleteProduct] = useState("");
  const latestRequestRef = useRef(0);
  const skipSearchEffectRef = useRef(true);

  useEffect(() => {
    getCatalogCategories();
  }, []);

  useEffect(() => {
    if (skipSearchEffectRef.current) {
      skipSearchEffectRef.current = false;
      return;
    }

    const activeCategoryId = getActiveCategoryId();

    if (!activeCategoryId) {
      setProducts([]);
      setTotal(0);
      setPages(0);
      return;
    }

    const timer = setTimeout(() => {
      getProductsByCatalogCategory(activeCategoryId, 1, search);
    }, 250);

    return () => clearTimeout(timer);
  }, [search]);

  const getCatalogCategories = () => {
    setLoading(true);

    const config = {
      method: "get",
      url: `${serverUrl}/catalog-categories/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
    };

    axios(config)
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        setCatalogCategories(rows);

        const defaultRoot = rows.find(
          (item) =>
            Number(item?.level || 0) === 1 &&
            String(item?.name || "").trim().toLowerCase() ===
              "frutas y verduras"
        );

        if (defaultRoot && defaultRoot._id) {
          const rootId = String(defaultRoot._id);
          setLevel1Id(rootId);
          setLevel2Id("");
          setLevel3Id("");
          setPage(1);
          getProductsByCatalogCategory(rootId, 1);
        } else {
          setProducts([]);
          setTotal(0);
          setPages(0);
          setLoading(false);
          toast(
            'No se encontró la categoría raíz "Frutas y Verduras".',
            darkToast
          );
        }
      })
      .catch(() => {
        setLoading(false);
        toast("Couldn't load catalog categories.", darkToast);
      });
  };

  const level1Options = catalogCategories
    .filter((item) => Number(item?.level || 0) === 1)
    .sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base",
      })
    );

  const level2Options = catalogCategories
    .filter(
      (item) =>
        Number(item?.level || 0) === 2 &&
        String(item?.parentId || "") === String(level1Id)
    )
    .sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base",
      })
    );

  const level3Options = catalogCategories
    .filter(
      (item) =>
        Number(item?.level || 0) === 3 &&
        String(item?.parentId || "") === String(level2Id)
    )
    .sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base",
      })
    );

  const getActiveCategoryId = () => {
    if (level3Id) return level3Id;
    if (level2Id) return level2Id;
    if (level1Id) return level1Id;
    return "";
  };

  const getProductsByCatalogCategory = (catalogCategoryId, targetPage = 1, searchOverride = null) => {
    if (!catalogCategoryId) {
      setProducts([]);
      setTotal(0);
      setPages(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    const requestId = ++latestRequestRef.current;

    const effectiveSearch =
      searchOverride !== null ? searchOverride : search;

    const config = {
      method: "post",
      url: `${serverUrl}/products/get-products-by-catalog-category`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        catalogCategoryId,
        page: targetPage,
        limit,
        search: effectiveSearch,
        sortBy: "_id",
        sortOrder: "desc",
      },
    };

    axios(config)
      .then((response) => {
        if (requestId !== latestRequestRef.current) {
          return;
        }

        const payload = response.data || {};
        const items = Array.isArray(payload.items) ? payload.items : [];
        const pagination = payload.pagination || {};

        setProducts(items);
        setPage(Number(pagination.page || targetPage || 1));
        setTotal(Number(pagination.total || 0));
        setPages(Number(pagination.pages || 0));
        setLoading(false);
      })
      .catch(() => {
        if (requestId !== latestRequestRef.current) {
          return;
        }
        setLoading(false);
        toast("Couldn't load filtered products.", darkToast);
      });
  };

  const refreshCurrentFilter = (targetPage = page) => {
    const activeCategoryId = getActiveCategoryId();

    if (activeCategoryId) {
      getProductsByCatalogCategory(activeCategoryId, targetPage);
      return;
    }

    setProducts([]);
    setTotal(0);
    setPages(0);
  };

  const removeProduct = () => {
    setLoading(true);

    const config = {
      method: "post",
      url: `${serverUrl}/products/remove`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        productId: deleteProduct._id,
      },
    };

    axios(config)
      .then(() => {
        toast("Product deleted successfully!", darkToast);
        setDeleteProduct("");
        refreshCurrentFilter(1);
      })
      .catch(() => {
        toast("Something went wrong", darkToast);
        setLoading(false);
      });
  };

  const handleLevel1Change = (value) => {
    setLevel1Id(value);
    setLevel2Id("");
    setLevel3Id("");
    setPage(1);

    if (value) {
      getProductsByCatalogCategory(value, 1);
    } else {
      setProducts([]);
      setTotal(0);
      setPages(0);
    }
  };

  const handleLevel2Change = (value) => {
    setLevel2Id(value);
    setLevel3Id("");
    setPage(1);

    if (value) {
      getProductsByCatalogCategory(value, 1);
    } else if (level1Id) {
      getProductsByCatalogCategory(level1Id, 1);
    } else {
      setProducts([]);
      setTotal(0);
      setPages(0);
    }
  };

  const handleLevel3Change = (value) => {
    setLevel3Id(value);
    setPage(1);

    if (value) {
      getProductsByCatalogCategory(value, 1);
    } else if (level2Id) {
      getProductsByCatalogCategory(level2Id, 1);
    } else if (level1Id) {
      getProductsByCatalogCategory(level1Id, 1);
    } else {
      setProducts([]);
      setTotal(0);
      setPages(0);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      refreshCurrentFilter(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < pages) {
      refreshCurrentFilter(page + 1);
    }
  };

  return (
    <>
      <Route exact path="/products">
        <Modal
          isOpen={deleteProduct !== ""}
          style={{
            content: {
              borderRadius: "1rem",
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
            },
            overlay: {
              backgroundColor: "rgba(0,0,0,0.7)",
            },
          }}
        >
          <div className="modalContent">
            <p className="modalTitle">Delete Product?</p>
            <p className="modalBody">
              Are you sure, want to delete <b>{`${deleteProduct.title}`}</b> ?
            </p>

            {loading ? (
              <>
                <br />
                <ReactLoading
                  type={"spinningBubbles"}
                  color={"var(--primaryColor)"}
                  width={"2rem"}
                  height={"2rem"}
                />
              </>
            ) : (
              <div className="modalButtonsRow">
                <button onClick={() => setDeleteProduct("")}>Cancel</button>
                <button
                  onClick={() => {
                    setLoading(true);
                    removeProduct();
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </Modal>

        <div className="content-body">
          <div className="content-title">
            <p>Products</p>
            <button
              onClick={() => {
                history.push("/products/new");
              }}
            >
              + Add New Product
            </button>
          </div>

          <div className="filter-select">
<div style={{ marginTop: "10px" }}>
  <input
    type="text"
    placeholder="Buscar producto..."
    value={search}
    onChange={(e) => {
      const value = e.target.value;
      setSearch(value);
      setPage(1);
    }}
    style={{
      padding: "8px",
      width: "250px",
      borderRadius: "6px",
      border: "1px solid #ccc",
    }}
  />
</div>
            <p>Catalog Category:</p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <select
                value={level1Id}
                onChange={(e) => handleLevel1Change(e.target.value)}
              >
                <option value="">Main Category</option>
                {level1Options.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>

              <select
                value={level2Id}
                onChange={(e) => handleLevel2Change(e.target.value)}
                disabled={!level1Id}
              >
                <option value="">Subcategory</option>
                {level2Options.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>

              <select
                value={level3Id}
                onChange={(e) => handleLevel3Change(e.target.value)}
                disabled={!level2Id}
              >
                <option value="">Final Category</option>
                {level3Options.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ margin: "10px 0 20px 0", fontSize: "0.95rem" }}>
            <b>Total:</b> {total} &nbsp; | &nbsp;
            <b>Page:</b> {page} / {pages || 1}
          </div>

          <div
            style={{
              height: loading ? "70vh" : "",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loading ? (
              <ReactLoading
                type={"spinningBubbles"}
                color={"var(--primaryColor)"}
                width={"4rem"}
                height={"4rem"}
              />
            ) : products.length === 0 ? (
              <p>No Products</p>
            ) : (
              <table>
                <tr>
                  <th>No.</th>
                  <th>Name</th>
                  <th>Image</th>
                  <th>Category</th>
                  <th>Stock</th>
                  <th>Sold</th>
                  <th>Price</th>
                  <th>Created On</th>
                  <th>Edit</th>
                  <th>Delete</th>
                </tr>
                {products.map((product, index) => {
                  return (
                    <tr key={index}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>{product.name || product.title || ""}</td>
                      <td>
                        <img src={product.images[0]} alt="" />
                      </td>
                      <td>
                        {product.categorySnapshot?.name ||
                          product.categorySnapshot?.pathName ||
                          product.category?.name ||
                          ""}
                      </td>
                      <td>{product.stock}</td>
                      <td>{product.sold}</td>
                      <td>
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: product.currencyCode || "USD",
                        }).format(product.price)}
                      </td>
                      <td>{String(product.createdOn || product.createdAt || "").split("T")[0]}</td>
                      <td>
                        <button
                          onClick={() => {
                            history.push(`/products/edit/${product._id}`);
                          }}
                          style={{
                            background: "white",
                            border: "1px solid var(--primaryColor)",
                          }}
                        >
                          <FontAwesome
                            name="pencil"
                            style={{
                              fontSize: "1rem",
                              color: "var(--primaryColor)",
                            }}
                          />
                        </button>
                      </td>
                      <td>
                        <button onClick={() => setDeleteProduct(product)}>
                          <FontAwesome
                            name="trash"
                            style={{ fontSize: "1rem", color: "white" }}
                          />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </table>
            )}
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <button onClick={handlePrevPage} disabled={loading || page <= 1}>
              Previous
            </button>
            <button onClick={handleNextPage} disabled={loading || page >= pages}>
              Next
            </button>
          </div>

          <ToastContainer />
        </div>
      </Route>

      <Route path="/products/new" component={AddProduct} />
      <Route path="/products/edit/:productId" component={EditProduct} />
    </>
  );
}

export default Products;
